import React, { useEffect } from "react"
import { z } from "zod"
import { useAppCtx } from "../hooks/use-app-ctx"
import { Signal } from "../utils/signals"
import { usePubsub } from "../redux/pubsub"
import { AdaptiveBatcher } from "../utils/adaptive-batch"
import { create } from "zustand"
import { useSelector } from "../store"

const eventSchema = z.object({
  id: z.string(),
  name: z.string(),
  dataSourceId: z.string().optional()
})

export type BackendEvent = z.infer<typeof eventSchema>

type BackendEventsState = {
  eventSource: EventSource | null
  signal: Signal<BackendEvent>
  refCount: number

  connect: (url: string, userId?: string) => void
  disconnect: () => void
}

// Singleton so that there's only one connection even if there are components
export const useBackendEventsStore = create<BackendEventsState>((set, get) => ({
  eventSource: null,
  signal: new Signal<BackendEvent>(),
  refCount: 0,

  connect: (url) => {
    const { eventSource, signal } = get()

    if (eventSource) {
      throw new Error("Already connected to EventSource")
    }

    const newSource = new EventSource(url)

    newSource.addEventListener("app-event", e => {
      try {
        const event = eventSchema.parse(JSON.parse(e.data))
        signal.emit(event)
      } catch (e) {
        console.error("Error handling backend event", e);
      }
    })

    set({ eventSource: newSource })
  },

  disconnect: () => {
    get().eventSource?.close()
    set({ eventSource: null })
  }
}))

export function useBackendEvents() {
  const appCtx = useAppCtx()
  const signal = useBackendEventsStore(s => s.signal)
  const sseEnabled = useSelector(s => s.currentViewer?.config?.sseEnabled)

  useEffect(() => {
    if (!sseEnabled) return
    if (appCtx.type !== "wistor-backend") return

    const store = useBackendEventsStore
    store.setState(s => ({ refCount: s.refCount + 1 }))
    if (store.getState().eventSource == null) {
      const url = appCtx.makeUrl(ctx => `${ctx.origin}/servlets/cgi/api/events`)
      store.getState().connect(url)
    }

    return () => {
      store.setState(s => ({ refCount: s.refCount - 1 }))
      if (store.getState().refCount > 0) return
      store.getState().disconnect()
    }
  }, [ appCtx, sseEnabled ])

  return signal
}

export function BackendEventsVarPublisher(): React.ReactNode {
  const [, publish] = usePubsub()
  const signal = useBackendEvents()
  useEffect(() => {
    const batcher = new AdaptiveBatcher<string, BackendEvent>(publishBatch)

    signal.connect(handleEvent)
    return () => {
      batcher.cancel()
      signal.disconnect(handleEvent)
    }

    function publishBatch(batch: Map<string, BackendEvent>) {
      for (const [, event] of batch) {
        // should be a js-safe identifier for future support of liquid-js
        const varName = "event." + event.name.replace('-', '_')
        publish(varName, "time:" + Date.now(), "backend-event")
      }
    }

    function handleEvent(event: BackendEvent)  {
      // change this key when we have more detailed events (e.g. data-update of a graph)
      const eventKey = event.name
      batcher.push(eventKey, event)
    }
  }, [ signal, publish ])

  return null
}
